import React, {useEffect} from "react";
import {observer} from "mobx-react-lite";
import {useNavigate, useParams} from "react-router-dom";

import PageLayout from "../PageLayout";
import appStore from "../../storage/AppStore";
import clubStore from "../../storage/ClubStore";
import Btn from "../../ui/Btn";

import "../../common/game.css";

const GameTeamDetails = () => {
  const navigate = useNavigate();
  const {id} = useParams();

  useEffect(() => {
    if (id) clubStore.getGameTeamDetails(id);
  }, [id]);

  const details = clubStore.club.game_team_details;

  const renderSimpleList = (items, emptyText, getKey, renderText, icon) => {
    if (!items || !items.length) {
      return (
        <div className="game-card p-4 text-center">
          <i className={`${icon} text-2xl text-color-secondary mb-2`} aria-hidden="true"/>
          <div className="text-600">{emptyText}</div>
        </div>
      );
    }

    return (
      <div className="grid m-0">
        {items.map((item, index) => (
          <div key={getKey(item, index)} className="col-12 lg:col-6 p-2">
            <div className="game-card h-full flex align-items-center gap-3 p-3">
              <div className="flex align-items-center justify-content-center surface-ground border-round-lg p-3 flex-shrink-0">
                <i className={`${icon} text-primary text-xl`} aria-hidden="true"/>
              </div>
              <div className="min-w-0 flex-1 font-semibold line-height-3 overflow-wrap-anywhere">{renderText(item)}</div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderSection = (title, icon, count, content) => (
    <section className="mb-4">
      <div className="flex align-items-center gap-2 mb-3">
        <i className={`${icon} text-primary text-xl`} aria-hidden="true"/>
        <h4 className="m-0 font-bold text-lg">{title}</h4>
        <span className="badge badge-upcoming ml-auto">{count}</span>
      </div>
      {content}
    </section>
  );

  if (!details) {
    return (
      <PageLayout header={
        <div className="flex justify-content-between align-items-center w-full gap-2">
          <h3 className="m-0">{appStore.tr.game_team}</h3>
          <Btn icon="pi pi-arrow-left" onClick={() => navigate(-1)}/>
        </div>
      }>
        <div className="game-card p-4 text-center">
          <div className="text-600">{appStore.tr.not_found || "Not found"}</div>
        </div>
      </PageLayout>
    );
  }

  const membersCount = details.members_count ?? details.members?.length ?? 0;
  const tournamentsCount = details.tournaments_count ?? details.tournaments?.length ?? 0;
  const gamesCount = details.games_count ?? details.games?.length ?? 0;
  const awardsCount = details.awards_count ?? details.awards?.length ?? 0;
  const stats = [
    {icon: "pi pi-users", label: appStore.tr.players || "Players", count: membersCount},
    {icon: "pi pi-trophy", label: appStore.tr.tournaments || "Tournaments", count: tournamentsCount},
    {icon: "pi pi-flag", label: appStore.tr.games || "Games", count: gamesCount},
    {icon: "pi pi-star", label: appStore.tr.awards || "Awards", count: awardsCount}
  ];

  return (
    <PageLayout header={
      <div className="flex justify-content-between align-items-center gap-3 w-full">
        <h3 className="m-0 min-w-0 overflow-wrap-anywhere">{details.name}</h3>
        <Btn icon="pi pi-arrow-left" onClick={() => navigate(-1)}/>
      </div>
    }>
      <div className="game-card p-3 md:p-4 mb-4">
        <div className="flex align-items-center gap-3 mb-3">
          <div className="flex align-items-center justify-content-center surface-ground border-round-xl p-3 flex-shrink-0">
            <i className="pi pi-users text-primary text-2xl" aria-hidden="true"/>
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm text-color-secondary mb-1">{appStore.tr.game_team}</div>
            <div className="text-xl font-bold line-height-3 overflow-wrap-anywhere">{details.name}</div>
          </div>
        </div>
        <div className="grid m-0">
          {stats.map((stat) => (
            <div className="col-12 sm:col-6 p-2" key={stat.label}>
              <div className="competition-detail-item flex align-items-center justify-content-between gap-3 h-full">
                <div className="flex align-items-center gap-2 min-w-0">
                  <i className={`${stat.icon} text-primary flex-shrink-0`} aria-hidden="true"/>
                  <span className="text-color-secondary line-height-3 overflow-wrap-anywhere">{stat.label}</span>
                </div>
                <span className="font-bold text-xl flex-shrink-0">{stat.count}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {renderSection(appStore.tr.players || "Players", "pi pi-users", membersCount,
        renderSimpleList(details.members, appStore.tr.not_found || "No players",
          (m, i) => m.id || m.company_user_id || m.user_id || i,
          (m) => m.title || m.company_user?.title || m.user?.title || m.name || "-", "pi pi-user"))}

      {renderSection(appStore.tr.tournaments || "Tournaments", "pi pi-trophy", tournamentsCount,
        renderSimpleList(details.tournaments, appStore.tr.not_found || "No tournaments",
          (t, i) => t.id || t.name || i,
          (t) => t.name || "-", "pi pi-trophy"))}

      {renderSection(appStore.tr.games || "Games", "pi pi-flag", gamesCount,
        renderSimpleList(details.games, appStore.tr.not_found || "No games",
          (g, i) => g.id || i,
          (g) => {
            const title = g.name || "-";
            const time = g.start_time ? ` (${g.start_time})` : "";
            return `${title}${time}`;
          }, "pi pi-calendar"))}

      {renderSection(appStore.tr.awards || "Awards", "pi pi-star", awardsCount,
        renderSimpleList(details.awards, appStore.tr.no_awards_yet || "No awards",
          (a, i) => a.id || i,
          (a) => a.name || a.title || a.award_type?.name || "-", "pi pi-star"))}
    </PageLayout>
  );
};

export default observer(GameTeamDetails);
